import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Upload, FileSpreadsheet, ArrowLeft, ArrowRight, Download, CheckCircle2,
  AlertTriangle, XCircle, Loader2, Zap, ChevronDown, ChevronUp,
  FileWarning, Shield, MapPin, Hash, Copy, Ban, RotateCcw,
  Clock, ShieldAlert, Receipt
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Upload", "Preview", "Map Fields", "Audit & Import"];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-1" data-testid="expense-step-indicator">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            i < current ? "bg-[#10b981]/10 text-[#10b981]" :
            i === current ? "bg-[#4338ca] text-white" :
            "bg-[#f1f5f9] text-[#94a3b8]"
          }`} data-testid={`expense-step-${i}`}>
            {i < current ? <CheckCircle2 className="h-3 w-3" /> : <span className="w-4 text-center">{i + 1}</span>}
            <span className="hidden sm:inline">{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`w-6 h-px mx-1 ${i < current ? "bg-[#10b981]" : "bg-[#e2e8f0]"}`} />}
        </div>
      ))}
    </div>
  );
}

function UploadStep({ onParsed }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/import-expenses/sample-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url; a.download = "bizflowa_expense_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Template downloaded!");
    } catch { toast.error("Failed to download template"); }
  };

  const processFile = async (file) => {
    if (!file || !file.name.endsWith(".csv")) { toast.error("Please upload a .csv file"); return; }
    setLoading(true);
    try {
      const text = await file.text();
      const r = await api.post("/import-expenses/parse-csv", { csv_content: text });
      onParsed({ ...r.data, csv_content: text });
      toast.success(`Parsed ${r.data.total_rows} rows`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to parse CSV"); }
    finally { setLoading(false); }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0f172a]">Upload Expense Data</h2>
          <p className="text-sm text-[#64748b] mt-0.5">Upload a CSV file with your expense records</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-expense-template-btn"
          className="border-[#4338ca]/20 text-[#4338ca] hover:bg-[#4338ca]/5 rounded-lg">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Download Sample Template
        </Button>
      </div>
      <div onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDrop}
        data-testid="expense-csv-drop-zone"
        className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer ${dragging ? "border-[#4338ca] bg-[#4338ca]/5" : "border-[#e2e8f0] hover:border-[#cbd5e1] bg-[#f8fafc]"}`}>
        <input type="file" accept=".csv" onChange={(e) => { processFile(e.target.files?.[0]); e.target.value = ""; }} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="expense-csv-file-input" />
        {loading ? (
          <div className="flex flex-col items-center gap-3"><Loader2 className="h-10 w-10 text-[#4338ca] animate-spin" /><p className="text-sm text-[#64748b]">Reading CSV file...</p></div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-[#4338ca]/10 flex items-center justify-center"><Upload className="h-6 w-6 text-[#4338ca]" /></div>
            <div><p className="text-sm font-medium text-[#0f172a]">Drop your CSV file here, or click to browse</p><p className="text-xs text-[#94a3b8] mt-1">Supports .csv files with expense data</p></div>
          </div>
        )}
      </div>
      <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4">
        <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">Expected columns</p>
        <div className="flex flex-wrap gap-1.5">
          {["Vendor Name", "Date", "Category", "Description", "Amount", "GST Number", "GST Rate", "CGST", "SGST", "IGST", "Paid Via", "Payment Status", "Reverse Charge", "ITC Eligible"].map((col) => (
            <span key={col} className="px-2 py-1 bg-white border border-[#e2e8f0] rounded-md text-[11px] text-[#64748b] font-mono">{col}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PreviewStep({ data }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-semibold text-[#0f172a]">Data Preview</h2><p className="text-sm text-[#64748b]">Showing first {data.preview.length} of {data.total_rows} rows</p></div>
        <Badge className="bg-[#4338ca]/10 text-[#4338ca]"><FileSpreadsheet className="h-3 w-3 mr-1" /> {data.total_rows} rows detected</Badge>
      </div>
      <div className="overflow-x-auto border border-[#e2e8f0] rounded-xl">
        <table className="w-full text-xs" data-testid="expense-preview-table">
          <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-10">#</th>
            {data.headers.map((h, i) => (<th key={i} className="py-2.5 px-3 text-left text-[#64748b] font-medium whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>{data.preview.map((row, rIdx) => (
            <tr key={rIdx} className="border-b border-[#f1f5f9] last:border-0" data-testid={`expense-preview-row-${rIdx}`}>
              <td className="py-2 px-3 text-[#94a3b8] font-mono">{rIdx + 1}</td>
              {row.map((cell, cIdx) => (<td key={cIdx} className="py-2 px-3 text-[#0f172a] whitespace-nowrap max-w-[200px] truncate">{cell || <span className="text-[#cbd5e1]">—</span>}</td>))}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

function MappingStep({ data, mapping, setMapping }) {
  const { headers, bizflowa_fields } = data;
  return (
    <div className="space-y-4">
      <div><h2 className="text-lg font-semibold text-[#0f172a]">Map Your Columns</h2><p className="text-sm text-[#64748b]">Match your CSV columns to BizFlowa expense fields</p></div>
      <div className="border border-[#e2e8f0] rounded-xl overflow-hidden" data-testid="expense-mapping-table">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <th className="py-3 px-4 text-left text-[#64748b] font-medium w-1/2">BizFlowa Field</th>
            <th className="py-3 px-4 text-left text-[#64748b] font-medium w-1/2">Your CSV Column</th>
          </tr></thead>
          <tbody>{bizflowa_fields.map((bf) => {
            const isMatched = !!mapping[bf];
            return (
              <tr key={bf} className="border-b border-[#f1f5f9] last:border-0">
                <td className="py-2.5 px-4"><div className="flex items-center gap-2">
                  {isMatched ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981]" /> : <div className="h-3.5 w-3.5 rounded-full border-2 border-[#cbd5e1]" />}
                  <span className={`text-sm font-medium ${isMatched ? "text-[#0f172a]" : "text-[#94a3b8]"}`}>{bf}</span>
                  {["Vendor Name", "Amount"].includes(bf) && <span className="text-[9px] text-red-400 font-bold">REQ</span>}
                  {bf === "ITC Eligible" && <TooltipProvider><Tooltip><TooltipTrigger><ShieldAlert className="h-3 w-3 text-[#f59e0b]" /></TooltipTrigger><TooltipContent><p className="text-xs">Food, Personal & Motor Vehicle categories auto-block ITC</p></TooltipContent></Tooltip></TooltipProvider>}
                  {bf === "Reverse Charge" && <TooltipProvider><Tooltip><TooltipTrigger><RotateCcw className="h-3 w-3 text-[#4338ca]" /></TooltipTrigger><TooltipContent><p className="text-xs">RCM: Tax is a liability, not input credit</p></TooltipContent></Tooltip></TooltipProvider>}
                </div></td>
                <td className="py-2.5 px-4">
                  <Select value={mapping[bf] || "_none"} onValueChange={(v) => {
                    const next = { ...mapping }; if (v === "_none") delete next[bf]; else next[bf] = v; setMapping(next);
                  }}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`expense-mapping-${bf.replace(/\s/g, "-").toLowerCase()}`}><SelectValue placeholder="— Not mapped —" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none"><span className="text-[#94a3b8]">— Not mapped —</span></SelectItem>
                      {headers.map((h) => (<SelectItem key={h} value={h}>{h}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
    </div>
  );
}

function AuditStep({ auditResult, onCommitValid, onRetry, committing }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showFilter, setShowFilter] = useState("all");
  if (!auditResult) return null;

  const { total_rows, valid_count, invalid_count, error_summary, rows } = auditResult;
  const toggleRow = (n) => { const s = new Set(expandedRows); s.has(n) ? s.delete(n) : s.add(n); setExpandedRows(s); };
  const filtered = rows.filter((r) => {
    if (showFilter === "errors") return !r.valid;
    if (showFilter === "warnings") return r.warnings.length > 0 && r.valid;
    if (showFilter === "valid") return r.valid && r.warnings.length === 0;
    return true;
  });

  const complianceWarnings = rows.filter(r => r.warnings.some(w => ["itc_blocked", "rcm_flag", "pos_mismatch"].includes(w.type)));

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="expense-audit-summary">
        <Card className="border-[#e2e8f0]"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#0f172a]">{total_rows}</p><p className="text-[11px] text-[#64748b] mt-0.5">Total Rows</p></CardContent></Card>
        <Card className="border-[#10b981]/20 bg-[#10b981]/5"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#10b981]">{valid_count}</p><p className="text-[11px] text-[#10b981] mt-0.5">Ready to Import</p></CardContent></Card>
        <Card className="border-red-200 bg-red-50"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-red-500">{invalid_count}</p><p className="text-[11px] text-red-400 mt-0.5">Has Errors</p></CardContent></Card>
        <Card className="border-[#f59e0b]/20 bg-[#f59e0b]/5"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#f59e0b]">{complianceWarnings.length}</p><p className="text-[11px] text-[#f59e0b] mt-0.5">Compliance Flags</p></CardContent></Card>
      </div>

      {/* Error Breakdown */}
      {(invalid_count > 0 || complianceWarnings.length > 0) && (
        <div className="space-y-3">
          {invalid_count > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2" data-testid="expense-error-breakdown">
              <p className="text-sm font-semibold text-red-700">Issues Found</p>
              <div className="flex flex-wrap gap-2">
                {error_summary.pos_mismatch > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><MapPin className="h-3 w-3" /> {error_summary.pos_mismatch} inter-state credit errors</Badge>}
                {error_summary.missing_fields > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><FileWarning className="h-3 w-3" /> {error_summary.missing_fields} missing/invalid fields</Badge>}
                {error_summary.duplicate > 0 && <Badge className="bg-red-100 text-red-600 gap-1"><Copy className="h-3 w-3" /> {error_summary.duplicate} duplicates</Badge>}
              </div>
            </div>
          )}
          {complianceWarnings.length > 0 && (
            <div className="bg-[#f59e0b]/5 border border-[#f59e0b]/20 rounded-xl p-4 space-y-2" data-testid="expense-compliance-warnings">
              <p className="text-sm font-semibold text-[#f59e0b]">Compliance Guardrails</p>
              <div className="flex flex-wrap gap-2">
                {rows.filter(r => r.warnings.some(w => w.type === "itc_blocked")).length > 0 && (
                  <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] gap-1"><Ban className="h-3 w-3" /> {rows.filter(r => r.warnings.some(w => w.type === "itc_blocked")).length} ITC blocked (Sec 17(5))</Badge>
                )}
                {rows.filter(r => r.warnings.some(w => w.type === "rcm_flag")).length > 0 && (
                  <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] gap-1"><RotateCcw className="h-3 w-3" /> {rows.filter(r => r.warnings.some(w => w.type === "rcm_flag")).length} Reverse Charge (liability)</Badge>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        {[
          { key: "all", label: `All (${total_rows})` },
          { key: "errors", label: `Errors (${invalid_count})` },
          { key: "warnings", label: `Flagged (${rows.filter(r => r.warnings.length > 0).length})` },
          { key: "valid", label: `Clean (${rows.filter(r => r.valid && r.warnings.length === 0).length})` },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setShowFilter(tab.key)} data-testid={`expense-filter-${tab.key}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${showFilter === tab.key ? "bg-[#4338ca] text-white" : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"}`}>{tab.label}</button>
        ))}
      </div>

      {/* Row Results */}
      <div className="border border-[#e2e8f0] rounded-xl overflow-hidden max-h-[340px] overflow-y-auto" data-testid="expense-audit-rows">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10"><tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-12">Row</th>
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium">Vendor</th>
            <th className="py-2.5 px-3 text-left text-[#64748b] font-medium">Category</th>
            <th className="py-2.5 px-3 text-right text-[#64748b] font-medium">Amount</th>
            <th className="py-2.5 px-3 text-center text-[#64748b] font-medium w-16">ITC</th>
            <th className="py-2.5 px-3 text-center text-[#64748b] font-medium w-16">RCM</th>
            <th className="py-2.5 px-3 text-center text-[#64748b] font-medium w-20">Status</th>
            <th className="py-2.5 px-3 w-8" />
          </tr></thead>
          <tbody>
            {filtered.map((r) => {
              const hasIssues = r.errors.length > 0 || r.warnings.length > 0;
              return (
                <tr key={r.row} className="border-b border-[#f1f5f9] last:border-0" data-testid={`expense-audit-row-${r.row}`}>
                  <td className="py-2 px-3 font-mono text-[#94a3b8] align-top">{r.row}</td>
                  <td className="py-2 px-3 text-[#0f172a] font-medium align-top">{r.data.vendor_name || "—"}</td>
                  <td className="py-2 px-3 text-[#64748b] align-top">{r.data.category || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-[#0f172a] align-top">{formatINR(r.data.amount)}</td>
                  <td className="py-2 px-3 text-center align-top">
                    {r.data.is_eligible_itc
                      ? <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]">Yes</Badge>
                      : <TooltipProvider><Tooltip><TooltipTrigger><Badge className="bg-red-100 text-red-500 text-[10px]">Blocked</Badge></TooltipTrigger><TooltipContent><p className="text-xs">Blocked Credit — Section 17(5) CGST Act</p></TooltipContent></Tooltip></TooltipProvider>
                    }
                  </td>
                  <td className="py-2 px-3 text-center align-top">
                    {r.data.reverse_charge
                      ? <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[10px]">RCM</Badge>
                      : <span className="text-[#cbd5e1]">—</span>
                    }
                  </td>
                  <td className="py-2 px-3 text-center align-top">
                    {r.valid ? (r.warnings.length > 0 ? <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]">Warn</Badge> : <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]">OK</Badge>) : <Badge className="bg-red-100 text-red-500 text-[10px]">Error</Badge>}
                  </td>
                  <td className="py-2 px-3 align-top">{hasIssues && <button onClick={() => toggleRow(r.row)} className="text-[#94a3b8] hover:text-[#64748b]">{expandedRows.has(r.row) ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>}</td>
                </tr>
              );
            })}
            {filtered.map((r) => expandedRows.has(r.row) ? (
              <tr key={`${r.row}-d`} className="bg-[#fefce8]"><td colSpan={8} className="px-6 py-2"><div className="space-y-1">
                {r.errors.map((e, i) => <div key={i} className="flex items-center gap-2 text-xs text-red-600"><XCircle className="h-3 w-3 shrink-0" /> {e.message}</div>)}
                {r.warnings.map((w, i) => <div key={i} className="flex items-center gap-2 text-xs text-[#f59e0b]"><AlertTriangle className="h-3 w-3 shrink-0" /> {w.message}</div>)}
              </div></td></tr>
            ) : null)}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2" data-testid="expense-audit-actions">
        <Button variant="outline" onClick={onRetry} className="rounded-xl" data-testid="expense-fix-retry-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Fix Errors & Retry</Button>
        <Button onClick={onCommitValid} disabled={committing || valid_count === 0} data-testid="expense-import-valid-btn" className="bg-[#10b981] hover:bg-[#059669] rounded-xl shadow-sm">
          {committing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          {committing ? "Importing..." : `Import ${valid_count} Valid Expense${valid_count !== 1 ? "s" : ""}`}
        </Button>
      </div>
    </div>
  );
}

export default function ExpenseImporterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [csvData, setCsvData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const handleParsed = (data) => { setCsvData(data); setMapping(data.suggested_mapping || {}); setStep(1); };

  const runAudit = async () => {
    if (!csvData) return;
    setAuditing(true);
    try {
      const r = await api.post("/import-expenses/audit", { csv_content: csvData.csv_content, mapping });
      setAuditResult(r.data); setStep(3);
    } catch (err) { toast.error(err.response?.data?.detail || "Audit failed"); }
    finally { setAuditing(false); }
  };

  const commitValid = async () => {
    if (!auditResult) return;
    setCommitting(true);
    try {
      const validRows = auditResult.rows.filter(r => r.valid).map(r => r.data);
      const r = await api.post("/import-expenses/commit", { rows: validRows });
      toast.success(`Imported ${r.data.imported_count} expenses!`);
      setTimeout(() => navigate("/expenses"), 1200);
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    finally { setCommitting(false); }
  };

  const handleRetry = () => { setAuditResult(null); setStep(0); setCsvData(null); setMapping({}); };
  const canProceed = useMemo(() => { if (step === 1) return true; if (step === 2) return Object.keys(mapping).length >= 2; return false; }, [step, mapping]);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto" data-testid="expense-importer-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/expenses")} className="text-[#64748b] hover:text-[#0f172a]" data-testid="back-to-expenses-btn"><ArrowLeft className="h-4 w-4" /></Button>
          <div>
            <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Expense Data Importer</h1>
            <p className="text-sm text-[#64748b] mt-0.5">Import expenses with ITC, RCM & POS compliance checks</p>
          </div>
        </div>
      </div>
      <StepIndicator current={step} />
      <Card className="bg-white border-[#e2e8f0] shadow-sm"><CardContent className="p-6">
        {step === 0 && <UploadStep onParsed={handleParsed} />}
        {step === 1 && csvData && <PreviewStep data={csvData} />}
        {step === 2 && csvData && <MappingStep data={csvData} mapping={mapping} setMapping={setMapping} />}
        {step === 3 && <AuditStep auditResult={auditResult} onCommitValid={commitValid} onRetry={handleRetry} committing={committing} />}
      </CardContent></Card>
      {step > 0 && step < 3 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-xl" data-testid="expense-prev-step-btn"><ArrowLeft className="h-4 w-4 mr-2" /> Back</Button>
          {step === 2 ? (
            <Button onClick={runAudit} disabled={auditing || !canProceed} data-testid="expense-map-scan-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
              {auditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}{auditing ? "Scanning..." : "Map & Scan"}
            </Button>
          ) : (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed} data-testid="expense-next-step-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">Next <ArrowRight className="h-4 w-4 ml-2" /></Button>
          )}
        </div>
      )}
    </div>
  );
}
